import React, { useRef, useEffect, useState } from 'react';

interface BarcodeScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScanSuccess: (barcode: string) => void;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ isOpen, onClose, onScanSuccess }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }, // Use back camera on mobile
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
        setScanning(true);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Failed to access camera. Please grant camera permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setScanning(false);
  };

  const captureBarcode = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // In a real implementation, you would use a barcode detection library like:
    // - @zxing/library (ZXing)
    // - quagga2 (for 1D barcodes)
    // - BarcodeDetector API (browser native, but limited support)

    // For this demo, we'll simulate barcode detection
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    
    // Placeholder: In production, integrate a barcode library here
    // For now, allow manual entry
    const manualBarcode = prompt('Enter barcode manually (or integrate barcode library):');
    
    if (manualBarcode) {
      onScanSuccess(manualBarcode);
      stopCamera();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Scan Barcode</h3>
          <button
            onClick={() => {
              stopCamera();
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          {/* Camera Preview */}
          <div className="relative aspect-video overflow-hidden rounded-lg bg-gray-900">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="h-full w-full object-cover"
            />
            {scanning && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-32 w-64 border-2 border-green-500 rounded-lg animate-pulse" />
              </div>
            )}
          </div>

          {/* Hidden canvas for image processing */}
          <canvas ref={canvasRef} className="hidden" />

          {/* Controls */}
          <div className="flex gap-3">
            <button
              onClick={captureBarcode}
              disabled={!scanning}
              className="flex-1 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              📸 Capture Barcode
            </button>
            <button
              onClick={() => {
                stopCamera();
                onClose();
              }}
              className="rounded-lg border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          <div className="rounded-lg bg-blue-50 p-3 text-xs text-blue-800">
            <strong>Note:</strong> Position the barcode within the green box. For production use, integrate a barcode detection library like @zxing/library or quagga2.
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
